import { useEffect, useState } from "react";

import { Formik } from "formik";

import { useApiAccountsByUserOrgQuery } from "@sso/apis/apiAccounts";
import { useSearchOrganisationsMutation } from "@sso/apis/organisations";
import { useUsersByUserOrgQuery } from "@sso/apis/users";
import { MultiSelect, SelectDataType as MultiSelectDataType } from "@sso/components/Form/MultiSelect";
import { SelectDataType } from "@sso/components/Form/Select";
import { FormikCheckbox, FormikTextInput } from "@sso/components/Formik";
import FormikTypeahead from "@sso/components/Formik/FormikTypeahead";
import { UserRole } from "@sso/constant";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import { CustomForm } from "@sso/ui/CustomForm";
import { Flex } from "@sso/ui/FlexBox";
import { Typography } from "@sso/ui/Typography";

export interface BilledOperationFiltersProps {
  handleStartDateChange: (event: any) => void;
  handleEndDateChange: (event: any) => void;
  handleBillableOperationFilterCheckChange: (event: any) => void;
  handleIsChecked: (billableOperationKey: string) => boolean;
  handleUserFiltersChange: (selectedUsers: MultiSelectDataType[], selectedApiAccounts: MultiSelectDataType[]) => void;
  handleOrganisationFilterChange: (selectedOrganisation: number | null) => void;
  filterStartDate: Date;
  filterEndDate: Date;
}

export const BilledOperationsFilters = (props: BilledOperationFiltersProps) => {

  const user = useAppSelector(selectCurrentUser);
  const isAdmin = user?.role.roleId === UserRole.ADMINISTRATOR;
  const [searchOrganisations] = useSearchOrganisationsMutation();

  const [selectedOrganisation, setSelectedOrganisation] = useState<number | null>(null);
  const { data: apiAccounts } = useApiAccountsByUserOrgQuery(selectedOrganisation);
  const { data: userAccounts } = useUsersByUserOrgQuery(selectedOrganisation);

  const [selectedUserAccounts, setSelectedUserAccounts] = useState<MultiSelectDataType[]>([]);
  const [selectedApiAccounts, setSelectedApiAccounts] = useState<MultiSelectDataType[]>([]);

  const [organisationOptions, setOrganisationOptions] = useState<SelectDataType[]>([]);


  useEffect(() => {
    setSelectedUserAccounts([]);
    setSelectedApiAccounts([]);
    props.handleUserFiltersChange([], []);
    props.handleOrganisationFilterChange(selectedOrganisation);
  }, [selectedOrganisation]);


  if (apiAccounts === undefined || userAccounts === undefined) {
    return <></>;
  }

  const handleUserFilterChange = (users: MultiSelectDataType[]) => {
    setSelectedUserAccounts(users);
    props.handleUserFiltersChange(users, selectedApiAccounts);
  };

  const handleApiFilterChange = (apiAccounts: MultiSelectDataType[]) => {
    setSelectedApiAccounts(apiAccounts);
    props.handleUserFiltersChange(selectedUserAccounts, apiAccounts);
  };

  const handleOrganisationClear = () => {
    setOrganisationOptions([]);
    setSelectedOrganisation(null);
    setSelectedUserAccounts([]);
    setSelectedApiAccounts([]);
    props.handleUserFiltersChange([], []);
    props.handleOrganisationFilterChange(null);
  };

  const handleOrganisationSearch = (searchTerm: string) => {
    if (!searchTerm) {
      setOrganisationOptions([]);
      setSelectedOrganisation(null);
      return;
    }
    searchOrganisations(searchTerm).then(response => {
      if ("data" in response) {
        const organisations = response.data;
        const options = organisations.map(org => ({
          key: org.organisationId,
          value: org.organisationId,
          label: org.name,
        }));
        setOrganisationOptions(options);
      }
    });
  };

  return (
    <>
      <Formik initialValues={{}} onSubmit={() => { }}>
        <CustomForm style={{ height: "auto", textAlign: "left" }}>
          <div style={{ border: "1px solid #b2b2b2", borderRadius: 3, width: "100%", padding: "5px" }}>
            <Typography as="h4" textAlign="left" width="100%" style={{ marginTop: 5 }}>
              Date Filters:
            </Typography>
            <Flex style={{ paddingTop: 15, textAlign: "left", width: "100%" }}>
              <FormikTextInput
                onChange={props.handleStartDateChange}
                max={props.filterEndDate.toISOString().split("T")[0]}
                type="date"
                name={"startDate"}
                label="Start Date"
                value={props.filterStartDate.toISOString().split("T")[0]}
              />
              <FormikTextInput
                onChange={props.handleEndDateChange}
                min={props.filterStartDate.toISOString().split("T")[0]}
                type="date"
                name={"endDate"}
                label="End Date"
                value={props.filterEndDate.toISOString().split("T")[0]}
              />
            </Flex>
          </div>
          <div style={{ border: "1px solid #b2b2b2", borderRadius: 3, width: "100%", padding: "5px", marginTop: 10 }}>
            <Typography as="h4" textAlign="left" width="100%">
              Billable Operation Filters:
            </Typography>
            <Flex style={{ textAlign: "left", width: "100%" }}>
              <FormikCheckbox
                name={"geoLocation"}
                label="Geolocation Lookup"
                onChange={props.handleBillableOperationFilterCheckChange}
                checked={props.handleIsChecked("geoLocation")}
              />
              <FormikCheckbox
                name={"calculation"}
                label="EVR Calculation"
                onChange={props.handleBillableOperationFilterCheckChange}
                checked={props.handleIsChecked("calculation")}
              />
              <FormikCheckbox
                name={"rechargeRequest"}
                label="EVR Recharge Calculation"
                onChange={props.handleBillableOperationFilterCheckChange}
                checked={props.handleIsChecked("rechargeRequest")}
              />
            </Flex>
          </div>
          {isAdmin && <div style={{ border: "1px solid #b2b2b2", borderRadius: 3, width: "100%", padding: "5px", marginTop: 10 }}>
            <Typography as="h4" textAlign="left" width="100%" style={{ marginBottom: 10 }}>
              Organisation Filter:
            </Typography>
            <FormikTypeahead
              name={"organisationId"}
              label="Search Organisations..."
              items={organisationOptions}
              onSearch={handleOrganisationSearch}
              clear={handleOrganisationClear}
              handleChange={(value) => {
                // Convert to number
                const numericValue = Number(value);

                // Only set the state if it's a valid number
                if (!isNaN(numericValue)) {
                  setSelectedOrganisation(numericValue);
                }
              }}
            />
          </div>}
          <div style={{ border: "1px solid #b2b2b2", borderRadius: 3, width: "100%", padding: "5px", marginTop: 10 }}>
            <Typography as="h4" textAlign="left" width="100%">
              User Filters:
            </Typography>
            <Flex style={{ textAlign: "left", width: "100%", marginTop: "15px" }}>
              <MultiSelect
                label="User"
                items={userAccounts.map(x => ({ key: x.userId, value: x.fullName }))}
                searchable
                onChange={handleUserFilterChange}
                value={selectedUserAccounts}
              />
              <MultiSelect
                label="API Account"
                items={apiAccounts.map(x => ({ key: x.apiAccountId, value: x.name }))}
                searchable
                onChange={handleApiFilterChange}
                value={selectedApiAccounts}
              />
            </Flex>
          </div>
        </CustomForm>
      </Formik>
    </>
  );
};